import React, { useEffect, useRef, useState } from 'react';
import {
  RESULT_FEEDBACK,
  RowMenu,
  type DirectoryKind,
  type DirectoryRowAction,
} from '~/design-system/components/v1_5/Directory';
import {
  EditableText,
  PanelHeader,
  PanelHeaderButton,
  type PanelHeaderButtonProps,
} from '~/design-system/components/v1_5/Layout';
import styles from './PreviewBar.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * Must match `--hv15-dur-menu-out`. The menu stays mounted this long after it closes so the exit
 * transition has something to run on.
 *
 * The FOURTH copy of this shape in v1.5 — `HeaderV15`, `DirectoryPanel` and `ShareMenu` each carry
 * their own, keyed to their own menu state. All four should collapse into one hook.
 */
const MENU_EXIT_MS = 120;

/**
 * How wide the name may grow while it is being edited.
 *
 * At rest the name is a flex item that ellipsises, so it needs no ceiling of its own — the run
 * shrinks it. A field sized to its text has no such governor, so a long name would push the trailing
 * cluster off the pane, taking the directory fold toggle with it. 420 is the widest a name can be
 * before that becomes possible in the narrowest pane the layout allows.
 */
const TITLE_MAX = 420;

export interface PreviewBarProps {
  /**
   * A control (or a run of them) BEFORE the name, on the bar's left end. The directory fold toggle
   * lives here: the panel it opens is the one directly to the bar's left, and a control that acts
   * leftward reads wrong on the right edge (Guri, 2026-08-30). It renders whether or not there is a
   * title, because once the directory folds this bar is the only band still on screen and the
   * toggle in this slot is the only way back (D4).
   */
  leading?: React.ReactNode;
  /** The artifact's own name. Absent on the empty state, where the bar carries only its controls. */
  title?: string;
  /**
   * The state channel. `artifact 02`, `artifact 02 · writing`, `16 sections`, `1920 × 1080`,
   * `checked 5m ago` — the pane says what it is looking at and what is happening to it in one slot.
   */
  meta?: string;
  /**
   * The trailing cluster. A node rather than a fixed set of buttons, exactly as `PanelHeader` takes
   * one: which controls belong on this bar depends on what the host has actually built, and a bar
   * that hard-codes three would ship a trigger for whichever of them is not there yet.
   */
  trailing?: React.ReactNode;
  /**
   * A control AFTER the bar's own `⋯`, on its far right end — `leading`'s mirror. The Properties
   * toggle lives here (2026-09-24): the pane it opens docks directly to the bar's right, and a
   * control sits on the edge of the panel it acts on, the same rule that put the Directory toggle
   * at the far left (Guri, 2026-08-30). In `trailing` it would land left of the `⋯`, between the
   * item's own actions, where it reads as one more thing to do to the artifact rather than as the
   * door to the pane beside it.
   */
  end?: React.ReactNode;
  /**
   * Supply this and the bar grows its own `⋯` at the end of the trailing cluster (before `end`),
   * opening the SAME menu the item's Directory row opens — `RowMenu`, imported, not rebuilt.
   *
   * The import crosses folders, which is the point. The preview and the directory are two views of
   * one object, and "duplicate, download, delete" has to mean the same three things and read the
   * same way in both; two menus that merely look alike are one edit away from not. So the bar owns
   * the control rather than leaving it to `trailing`, where each host would compose its own.
   */
  onItemAction?: (action: DirectoryRowAction) => void;
  /**
   * The open item's real menu rows — the host's same per-item cut the Directory row shows
   * (`menuRowsFor`), so the two `⋯`s can never disagree about what the item offers. Absent, the
   * menu draws `RowMenu`'s base four.
   */
  itemMenuRows?: DirectoryRowAction[];
  /** Sharpens generic labels per kind, exactly as the row menu does (`Download .html`). */
  itemMenuKind?: DirectoryKind;
  /**
   * The name was changed. ONE click opens it, not two: this is a heading rather than a row in a
   * list, so a single press has nothing else to mean — there is no selection to make here and
   * nothing beside it to hit by accident. Omit it and the name is not editable.
   */
  onRename?: (next: string) => void;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The Preview pane's own 36px artifact bar: the artifact's name, and whatever controls the host puts
 * on the right of it.
 *
 * The bar belongs to the PANE, not to the shell. The layout boards draw one browser bar spanning the
 * directory and the preview together; the Preview page draws this one inside the pane, carrying its
 * own hairline, and D8 makes the dedicated page the winner. The practical difference is that the
 * name here is the artifact's, so it has to sit where the artifact does.
 *
 * It used to lead with a `Artifacts /` collection crumb. That is gone: the kind is already named at
 * the top of the Directory, one pane to the left and permanently on screen, and the crumb restated
 * it beside every artifact while its chevron implied a switcher this pane does not own. The bar says
 * what is open; the Directory says what it is one of.
 *
 * It is `PanelHeader` with a name in the leading run and nothing else — the 36px height, the 12/6
 * asymmetric padding, the 7px rhythm, the hairline and the shrink behaviour of the leading run are
 * all already correct there, and re-authoring them here would be a second panel band that drifts
 * from the first one within a week.
 */
export function PreviewBar({
  leading,
  title,
  meta,
  trailing,
  end,
  onItemAction,
  itemMenuRows,
  itemMenuKind,
  onRename,
  theme: themeProp,
  className,
}: PreviewBarProps) {
  const theme = useV15Theme(themeProp);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const [rendered, setRendered] = useState(false);
  const closing = rendered && !open;
  /** The `⋯` and its menu together: the region a press may land in without dismissing it. */
  const anchorRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }
    if (!rendered) return;
    const timer = setTimeout(() => setRendered(false), MENU_EXIT_MS);
    return () => clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Inside is skipped so pressing the `⋯` itself is left to its own click handler — a pointerdown
    // that closed first would make the click that follows reopen what it just shut.
    const onDown = (e: PointerEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const more = onItemAction ? (
    <span ref={anchorRef} className={styles.moreAnchor}>
      <PreviewBarButton
        icon="more"
        label="More actions"
        // A menu trigger is not a toggle: `on` would announce "pressed" in place of the menu that
        // actually appeared. `ariaExpanded` is the state that matters and it suppresses the other.
        ariaHasPopup="menu"
        ariaExpanded={open}
        theme={theme}
        onClick={() => setOpen((v) => !v)}
      />
      {rendered ? (
        <div className={styles.menuLayer} data-closing={closing}>
          <RowMenu
            rows={itemMenuRows}
            kind={itemMenuKind}
            renameable={onRename !== undefined}
            theme={theme}
            onAction={(action) => {
              // Rename is the menu's route into the same field the name's own click opens — the
              // panel does exactly this for its rows, and a host asked to "handle rename" here
              // would have nothing to handle it with.
              if (action === 'rename') {
                setOpen(false);
                setEditing(true);
                return;
              }
              // Result-feedback actions ("Copied") hold the menu open for their beat; RowMenu's
              // onSettled closes it. Everything else closes on the press, as before.
              if (RESULT_FEEDBACK[action] !== undefined) {
                onItemAction(action);
                return;
              }
              setOpen(false);
              onItemAction(action);
            }}
            onSettled={() => setOpen(false)}
          />
        </div>
      ) : null}
    </span>
  ) : null;

  return (
    <PanelHeader
      theme={theme}
      trailing={
        // One cluster on the band's 7px rhythm, in a fixed order: the host's controls, the item's
        // `⋯`, then `end` on the pane's edge. Kept in the cluster rather than given a box of its own
        // so the last control lands the band's 6px from the edge whichever of the three it is.
        trailing || more || end ? (
          <>
            {trailing}
            {more}
            {end}
          </>
        ) : undefined
      }
      className={className}
    >
      {/* The band's 12px left padding is text clearance; a 24px control carries its own optical
          margin, so the leading slot pulls itself back to land 6px from the pane edge — the same
          6px the trailing cluster's last control gets from the band's other side. */}
      {leading ? <span className={styles.lead}>{leading}</span> : null}
      {/* The name is the only part of the run allowed to give. An artifact name is the one string
          here with no upper bound, and the alternative to an ellipsis is the trailing cluster being
          pushed off the pane. */}
      {title ? (
        <EditableText
          className={styles.title}
          value={title}
          editing={editing}
          onEditingChange={setEditing}
          onCommit={(next) => onRename?.(next)}
          label={`Rename ${title}`}
          // Sized to the name so the meta beside it stays put, capped so a long one ellipsises
          // instead of pushing the trailing cluster off the pane — the same ceiling the resting
          // title lives under.
          maxWidth={TITLE_MAX}
          activate={onRename !== undefined}
        />
      ) : null}
      {meta ? <span className={styles.meta}>{meta}</span> : null}
    </PanelHeader>
  );
}

export type PreviewBarButtonProps = Omit<PanelHeaderButtonProps, 'active' | 'pressed'> & {
  /**
   * The surface this control names is open. Swaps the glyph for its `-fill` twin and announces
   * `aria-pressed`.
   *
   * Three-valued, and the third value is not an oversight. `true` and `false` both say "this is a
   * toggle" — lit and unlit — while OMITTING it says the control is not a toggle at all, which is
   * the honest answer for the bar's `More`: a menu trigger that announced "toggle button, not
   * pressed" would be reporting a state change on the button in place of the menu that actually
   * appeared. Defaulting to `false` would have put that on every action in the cluster.
   */
  on?: boolean;
  /**
   * The lit glyph. Defaults to `${icon}-fill`, which is the registry's own naming, so a control only
   * names one when its twin is filed under something else.
   */
  activeIcon?: string;
};

/**
 * The artifact bar's control: `PanelHeaderButton` with its `-fill` twin derived.
 *
 * ACTIVE SAYS ITSELF ONCE, the way it does everywhere else in v1.5 — the application header, the
 * directory, the panel slot. The filled glyph carries the whole state; nothing paints behind it.
 *
 * This bar was the exception for a while. Its lit controls are `Directory` and `Annotate`, and annotate
 * is a MODE, so the argument was that a mode changes what a click on the artifact MEANS and that is
 * more than a 15px glyph swap can carry (Beka, 2026-09-06). The chip that came out of it read as a
 * second element sitting on the bar rather than as the control being on, which is the reason the rest
 * of v1.5 does not do this, and it is gone again (Beka, 2026-09-18).
 *
 * Because the glyph changes rather than only its colour, `aria-pressed` comes from `IconSlot`'s own
 * `activeIcon` derivation: an on control and the dark ones beside it are different nodes in the
 * accessibility tree as well as on screen, with or without a chip behind them.
 */
export const PreviewBarButton = React.forwardRef<HTMLButtonElement, PreviewBarButtonProps>(
  function PreviewBarButton({ on, icon, activeIcon, className, ...rest }, ref) {
    return (
      <PanelHeaderButton
        ref={ref}
        icon={icon}
        activeIcon={activeIcon ?? `${icon}-fill`}
        active={on}
        className={className}
        {...rest}
      />
    );
  },
);
